import { useEffect, useRef, useState } from "react";
import { Loader2, Target, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api, USER_ID } from "../api";
import { badgeToast, melaToast, MelaCoin } from "./BadgesSheet";

const HOWTO_KEY = "xp_howto_carrom";
const W = 440;
const R_COIN = 11, R_STRIKER = 15, R_POCKET = 21, RIM = 14;
const FRICTION = 0.982, WALL_E = 0.72, HIT_E = 0.92, MIN_V = 0.045;
const POCKETS = [[RIM + 12, RIM + 12], [W - RIM - 12, RIM + 12], [RIM + 12, W - RIM - 12], [W - RIM - 12, W - RIM - 12]];
const BASE_Y = W - 74;
const PLAYERS = [
  { name: "Player 1", coin: "WHITE", color: "#F3E9D2" },
  { name: "Player 2", coin: "BLACK", color: "#4A342A" },
];

const initCoins = () => {
  const c = W / 2, coins = [{ kind: "queen", x: c, y: c, vx: 0, vy: 0, r: R_COIN, alive: true }];
  for (let k = 0; k < 6; k++) {
    const a = (k * 60 * Math.PI) / 180;
    coins.push({ kind: k % 2 ? "black" : "white", x: c + Math.cos(a) * (R_COIN * 2 + 1), y: c + Math.sin(a) * (R_COIN * 2 + 1), vx: 0, vy: 0, r: R_COIN, alive: true });
  }
  for (let k = 0; k < 12; k++) {
    const a = ((k * 30 + 15) * Math.PI) / 180;
    coins.push({ kind: k % 2 ? "white" : "black", x: c + Math.cos(a) * (R_COIN * 4 + 3), y: c + Math.sin(a) * (R_COIN * 4 + 3), vx: 0, vy: 0, r: R_COIN, alive: true });
  }
  return coins;
};

export const CarromChaupal = ({ refresh }) => {
  const [howto, setHowto] = useState(() => localStorage.getItem(HOWTO_KEY) !== "1");
  const [phase, setPhase] = useState("setup"); // setup | play | won
  const [turn, setTurn] = useState(0);
  const [left, setLeft] = useState({ white: 9, black: 9 });
  const [queenBy, setQueenBy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [winner, setWinner] = useState(null);
  const [reward, setReward] = useState(null);
  const canvasRef = useRef(null);
  const world = useRef(null); // {coins, striker, moving, aim, turn}
  const raf = useRef(0);
  const session = useRef(null);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const draw = () => {
    const cv = canvasRef.current;
    if (!cv || !world.current) return;
    const ctx = cv.getContext("2d");
    const w = world.current;
    const g = ctx.createLinearGradient(0, 0, W, W);
    g.addColorStop(0, "#D9A85C"); g.addColorStop(1, "#C08A3E");
    ctx.fillStyle = "#3A2415"; ctx.fillRect(0, 0, W, W);
    ctx.fillStyle = g; ctx.fillRect(RIM, RIM, W - RIM * 2, W - RIM * 2);
    ctx.strokeStyle = "#00000033"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(W / 2, W / 2, 62, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = "#B0343466"; ctx.beginPath(); ctx.arc(W / 2, W / 2, 16, 0, Math.PI * 2); ctx.stroke();
    [BASE_Y, W - BASE_Y].forEach((y) => {
      ctx.strokeStyle = "#00000040"; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(90, y); ctx.lineTo(W - 90, y); ctx.stroke();
    });
    POCKETS.forEach(([px, py]) => {
      ctx.fillStyle = "#241407"; ctx.beginPath(); ctx.arc(px, py, R_POCKET, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = "#00000066"; ctx.lineWidth = 2; ctx.stroke();
    });
    w.coins.forEach((cn) => {
      if (!cn.alive) return;
      ctx.beginPath(); ctx.arc(cn.x, cn.y, cn.r, 0, Math.PI * 2);
      ctx.fillStyle = cn.kind === "queen" ? "#B03434" : cn.kind === "white" ? "#F3E9D2" : "#4A342A";
      ctx.fill();
      ctx.strokeStyle = "#00000055"; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.beginPath(); ctx.arc(cn.x, cn.y, cn.r - 4, 0, Math.PI * 2);
      ctx.strokeStyle = cn.kind === "black" ? "#ffffff22" : "#00000022"; ctx.stroke();
    });
    const s = w.striker;
    if (s.alive) {
      ctx.beginPath(); ctx.arc(s.x, s.y, R_STRIKER, 0, Math.PI * 2);
      ctx.fillStyle = "#EDE3CB"; ctx.fill();
      ctx.strokeStyle = "#D4AF37"; ctx.lineWidth = 2.5; ctx.stroke();
      ctx.beginPath(); ctx.arc(s.x, s.y, 5, 0, Math.PI * 2); ctx.fillStyle = "#C08A3E"; ctx.fill();
    }
    if (w.aim && s.alive) {
      const dx = s.x - w.aim.x, dy = s.y - w.aim.y;
      const p = Math.min(Math.hypot(dx, dy), 130);
      const a = Math.atan2(dy, dx);
      ctx.strokeStyle = "#FFFFFFAA"; ctx.lineWidth = 2; ctx.setLineDash([5, 5]);
      ctx.beginPath(); ctx.moveTo(s.x, s.y);
      ctx.lineTo(s.x + Math.cos(a) * p * 1.4, s.y + Math.sin(a) * p * 1.4); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "#E25822";
      ctx.fillRect(RIM + 6, RIM + 6, (p / 130) * 90, 6);
      ctx.strokeStyle = "#00000055"; ctx.strokeRect(RIM + 6, RIM + 6, 90, 6);
    }
  };

  const bodies = () => [world.current.striker, ...world.current.coins].filter((b) => b.alive);

  const step = () => {
    const w = world.current;
    let movingAny = false;
    for (let sub = 0; sub < 2; sub++) {
      bodies().forEach((b) => {
        b.x += b.vx / 2; b.y += b.vy / 2;
        b.vx *= Math.pow(FRICTION, 0.5); b.vy *= Math.pow(FRICTION, 0.5);
        if (b.x < RIM + b.r) { b.x = RIM + b.r; b.vx = Math.abs(b.vx) * WALL_E; }
        if (b.x > W - RIM - b.r) { b.x = W - RIM - b.r; b.vx = -Math.abs(b.vx) * WALL_E; }
        if (b.y < RIM + b.r) { b.y = RIM + b.r; b.vy = Math.abs(b.vy) * WALL_E; }
        if (b.y > W - RIM - b.r) { b.y = W - RIM - b.r; b.vy = -Math.abs(b.vy) * WALL_E; }
        for (const [px, py] of POCKETS) {
          if (Math.hypot(b.x - px, b.y - py) < R_POCKET - 2) {
            b.alive = false; b.vx = b.vy = 0;
            w.pocketed.push(b === w.striker ? "striker" : b.kind);
          }
        }
      });
      const bs = bodies();
      for (let i = 0; i < bs.length; i++) {
        for (let j = i + 1; j < bs.length; j++) {
          const a = bs[i], b = bs[j];
          const dx = b.x - a.x, dy = b.y - a.y;
          const d = Math.hypot(dx, dy) || 0.001, minD = a.r + b.r;
          if (d < minD) {
            const nx = dx / d, ny = dy / d;
            const overlap = (minD - d) / 2;
            a.x -= nx * overlap; a.y -= ny * overlap;
            b.x += nx * overlap; b.y += ny * overlap;
            const ma = a.r > R_COIN ? 2 : 1, mb = b.r > R_COIN ? 2 : 1;
            const rvn = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
            if (rvn < 0) {
              const imp = (-(1 + HIT_E) * rvn) / (1 / ma + 1 / mb);
              a.vx -= (imp / ma) * nx; a.vy -= (imp / ma) * ny;
              b.vx += (imp / mb) * nx; b.vy += (imp / mb) * ny;
            }
          }
        }
      }
    }
    bodies().forEach((b) => { if (Math.hypot(b.vx, b.vy) > MIN_V) movingAny = true; else { b.vx = 0; b.vy = 0; } });
    draw();
    if (movingAny) raf.current = requestAnimationFrame(step);
    else settle();
  };

  const settle = () => {
    const w = world.current;
    w.moving = false;
    const me = PLAYERS[w.turn].coin.toLowerCase();
    const got = w.pocketed;
    const foul = got.includes("striker");
    const ownCount = got.filter((k) => k === me).length;
    if (got.includes("queen")) {
      setQueenBy(w.turn);
      toast("RANI POCKETED! The red queen is yours.", { style: { background: "#1A1208", color: "#E25822" } });
    }
    if (foul) {
      const back = w.coins.find((c) => !c.alive && c.kind === me);
      if (back) {
        back.alive = true;
        back.x = W / 2 + (Math.random() * 30 - 15); back.y = W / 2 + (Math.random() * 30 - 15);
        back.vx = back.vy = 0;
        toast("FOUL — striker in the pocket! One coin returns.", { style: { background: "#2A1206", color: "#E25822" } });
      } else {
        toast("FOUL — striker in the pocket!", { style: { background: "#2A1206", color: "#E25822" } });
      }
    }
    const wl2 = w.coins.filter((c) => c.alive && c.kind === "white").length;
    const bl2 = w.coins.filter((c) => c.alive && c.kind === "black").length;
    setLeft({ white: wl2, black: bl2 });
    if (wl2 === 0 || bl2 === 0) {
      const winIdx = wl2 === 0 ? 0 : 1;
      setWinner(PLAYERS[winIdx].name);
      setPhase("won");
      finishOnServer(PLAYERS[winIdx].name);
      return;
    }
    const keep = ownCount > 0 && !foul;
    if (!keep) {
      w.turn = (w.turn + 1) % 2;
      setTurn(w.turn);
    }
    w.striker.alive = true;
    w.striker.x = W / 2; w.striker.y = BASE_Y; w.striker.vx = 0; w.striker.vy = 0;
    w.pocketed = [];
    draw();
  };

  const finishOnServer = async (winnerName) => {
    try {
      const r = await api.post("/arcade/carrom/finish", {
        user_id: USER_ID, session_id: session.current, players: 2, winner: winnerName });
      setReward(r.data.mela_result);
      badgeToast(r.data.new_badges);
      refresh();
    } catch (e) { setReward({ error: true, message: e.response?.data?.detail || "" }); }
  };

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/arcade/session", { user_id: USER_ID, game: "carrom" });
      session.current = r.data.session_id;
      world.current = {
        coins: initCoins(),
        striker: { x: W / 2, y: BASE_Y, vx: 0, vy: 0, r: R_STRIKER, alive: true },
        pocketed: [], moving: false, aim: null, turn: 0,
      };
      setTurn(0); setLeft({ white: 9, black: 9 }); setQueenBy(null); setWinner(null); setReward(null);
      setPhase("play");
      requestAnimationFrame(draw);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open the board"); }
    setBusy(false);
  };

  const evPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * W, y: ((e.clientY - rect.top) / rect.height) * W };
  };

  const onDown = (e) => {
    const w = world.current;
    if (!w || w.moving || phase !== "play") return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const p = evPos(e);
    if (Math.abs(p.y - w.striker.y) < 34 && Math.abs(p.x - w.striker.x) < 34) {
      w.drag = "place";
    } else {
      w.drag = "aim";
      w.aim = p;
    }
    draw();
  };
  const onMove = (e) => {
    const w = world.current;
    if (!w || !w.drag || w.moving) return;
    const p = evPos(e);
    if (w.drag === "place") {
      w.striker.x = Math.min(W - 96, Math.max(96, p.x));
    } else {
      w.aim = p;
    }
    draw();
  };
  const onUp = () => {
    const w = world.current;
    if (!w || !w.drag) return;
    if (w.drag === "aim" && w.aim) {
      const dx = w.striker.x - w.aim.x, dy = w.striker.y - w.aim.y;
      const pow = Math.min(Math.hypot(dx, dy), 130);
      if (pow > 14) {
        const a = Math.atan2(dy, dx);
        w.striker.vx = Math.cos(a) * (pow / 9);
        w.striker.vy = Math.sin(a) * (pow / 9);
        w.moving = true;
        w.aim = null; w.drag = null;
        raf.current = requestAnimationFrame(step);
        return;
      }
    }
    w.aim = null; w.drag = null;
    draw();
  };

  return (
    <div data-testid="game-carrom" className="rounded-2xl bg-[#121212] border border-[#B07A5E]/50 p-5">
      <p className="text-[10px] tracking-[0.3em] text-[#D9A85C] font-bold">CARROM CHAUPAL · ਕੈਰਮ</p>
      <p className="text-xs text-white/50 mt-1">2 players, real striker physics. Clear your colour first — winner takes 40 Regal Coins for this passport.</p>

      {howto && (
        <div data-testid="carrom-howto" className="mt-4 rounded-xl border border-[#D4AF37]/30 bg-[#1A1208] p-4">
          <p className="text-[9px] tracking-[0.25em] text-[#D4AF37] font-black">HOW TO PLAY</p>
          <p className="text-xs text-white/70 mt-1.5 leading-relaxed">Drag the striker along the baseline to place it. Then pull back anywhere on the board like a slingshot and release to shoot. Pocket your colour to keep your turn. Striker in a pocket = foul (a coin comes back). First to clear all 9 of their coins wins!</p>
          <button data-testid="carrom-howto-ok" onClick={() => { localStorage.setItem(HOWTO_KEY, "1"); setHowto(false); }}
            className="mt-3 h-9 px-5 rounded-full bg-[#D4AF37] text-black text-[10px] font-black tracking-widest">GOT IT</button>
        </div>
      )}

      {!howto && phase === "setup" && (
        <button data-testid="carrom-start" onClick={start} disabled={busy}
          className="mt-4 w-full h-12 rounded-full bg-[#D9A85C] text-black font-black text-sm tracking-widest disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Target size={16} />} SET THE BOARD
        </button>
      )}

      {phase !== "setup" && (
        <>
          <div className="flex items-center justify-between gap-2 mt-4 flex-wrap text-[10px] font-black">
            {PLAYERS.map((p, i) => (
              <span key={i} data-testid={`carrom-player-${i}`}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${i === turn && phase === "play" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-white/50"}`}>
                <span className="w-2.5 h-2.5 rounded-full border border-black/50" style={{ background: p.color }} />
                {p.name.toUpperCase()} · {i === 0 ? left.white : left.black} LEFT
                {queenBy === i && <span className="text-[#E25822]">+RANI</span>}
              </span>
            ))}
          </div>
          <div className="mt-3 max-w-[500px] mx-auto rounded-xl overflow-hidden border border-[#8a6d1f]/40 select-none">
            <canvas ref={(cv) => { canvasRef.current = cv; if (cv && world.current) draw(); }}
              data-testid="carrom-canvas" width={W} height={W}
              className="w-full touch-none block cursor-crosshair"
              onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} />
          </div>
          {phase === "play" && (
            <p className="mt-2 text-center text-[10px] text-white/40 font-bold tracking-widest">
              {PLAYERS[turn].name.toUpperCase()} ({PLAYERS[turn].coin}) — DRAG STRIKER TO PLACE · PULL BACK &amp; RELEASE TO SHOOT
            </p>
          )}
          {phase === "won" && (
            <div data-testid="carrom-won" className="mt-4 rounded-xl border border-[#D4AF37]/50 bg-[#1C160A] p-4 text-center">
              <PartyPopper size={22} className="mx-auto text-[#D4AF37]" />
              <p className="font-display text-2xl font-bold mt-1">{winner} clears the board!</p>
              <p className="text-xs text-white/55 mt-0.5">ਸ਼ਾਬਾਸ਼! A proper chaupal victory.</p>
              {reward?.message && (
                <p data-testid="carrom-reward" className={`text-xs font-bold mt-1.5 flex items-center justify-center gap-1.5 ${reward.error ? "text-white/45" : "text-[#F0C742]"}`}>{!reward.error && <MelaCoin size={14} />} {reward.message}</p>
              )}
              <button data-testid="carrom-again" onClick={() => setPhase("setup")}
                className="mt-3 h-10 px-6 rounded-full border border-[#D9A85C]/70 text-[#D9A85C] text-[10px] font-black tracking-widest">PLAY AGAIN</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
